import { useEffect, useMemo, useRef } from "react";

import { FormProvider, UseFormReturn } from "react-hook-form";

import { Box, Grid } from "@mui/material";

import {
  CommunityNamesSelect,
  HomeDetailsForm,
  HomeLocationSelect,
  ManufacturerSelect,
  RoofConstructionYearSelect,
  RoofShapesSelect,
  SkirtingTypeSelect,
  HomeSizeInput,
  HomeHudNumberInput,
  ModelHomeInput,
  MakeCustomInput,
} from "@covertree/new-business-forms";
import {
  selectBasicQuestions,
  selectGeoCodeResult,
  selectIsBookConversion,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import {
  Form,
  FormHomeTypeSelect,
  FormModelYearAutocomplete,
  FormSectionHeading,
  SubmitButton,
} from "@covertree/ui";
import { isManufacturedHomePark } from "@covertree/utils";
import { FixturesAndFinishesRadioGroup } from "@covertree/new-business-forms";
import { HomeType } from "@covertree/new-business-graphql";
import Typography from "@mui/material/Typography";
import { useScrollToNextButton } from "@hooks/scroll/useScrollToNextButton";
import { NEW_POLICY_SUBMIT_BUTTON_ID } from "@consts/index";

interface HomeDetailsViewProps {
  form: UseFormReturn<HomeDetailsForm>;
  isTenantFlow: boolean;
  loading: boolean;
  onSubmit: (formValues: HomeDetailsForm) => void;
}

export function HomeDetailsView({
  form,
  isTenantFlow,
  loading,
  onSubmit,
}: HomeDetailsViewProps) {
  const currentYear = useRef(new Date().getFullYear());

  const geoCodeResult = useNewBusinessSelector(selectGeoCodeResult);

  const { homeType, modelYear, totalSquareFootage, location, roofYearYyyy } =
    useNewBusinessSelector(selectBasicQuestions);

  const isBookConversion = useNewBusinessSelector(selectIsBookConversion);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitSuccessful },
    trigger,
    watch,
    setValue,
    getValues,
  } = form;

  const formRoofYearValue = watch("roofYearYyyy");
  const formLocationValue = watch("location");
  const formModelYearValue = watch("modelYear")?.value;
  const formHomeFixturesValue = watch("homeFixtures");
  const formHomeTypeValue = watch("homeType");
  const formManufacturerValue = watch("manufacturer");

  const triggerValidationOnConstructionYearValue = useMemo(
    () =>
      formRoofYearValue &&
      formModelYearValue &&
      formRoofYearValue >= formModelYearValue &&
      errors?.roofYearYyyy,
    [formRoofYearValue, errors, formModelYearValue]
  );

  const showParkNameSection = useMemo(
    () => isManufacturedHomePark(formLocationValue),
    [formLocationValue]
  );
  const isParkNameSectionDisabled = useMemo(
    () =>
      geoCodeResult?.homeLocation && !isBookConversion
        ? formLocationValue &&
          isManufacturedHomePark(geoCodeResult.homeLocation)
        : false,
    [geoCodeResult, formLocationValue, isBookConversion]
  );

  const showHomeHudAndSkirtingType = useMemo(
    () =>
      !isTenantFlow &&
      [
        HomeType.SingleWide,
        HomeType.DoubleWide,
        HomeType.TripleWide,
        HomeType.ParkModel,
        HomeType.Duplex,
        HomeType.CrossMod,
      ].includes(formHomeTypeValue),
    [isTenantFlow, formHomeTypeValue]
  );

  const showMakeCustom = useMemo(
    () => formManufacturerValue?.value === "Not Listed",
    [formManufacturerValue]
  );

  useEffect(() => {
    if (triggerValidationOnConstructionYearValue) {
      trigger("roofYearYyyy");
    }
  }, [triggerValidationOnConstructionYearValue, trigger]);

  useEffect(() => {
    if (!isBookConversion) return;

    if (getValues("homeType") === "Unknown") {
      setValue("homeType", null);
    }
    if (getValues("location") === "Unknown") {
      setValue("location", null);
    }
    trigger();
  }, [isBookConversion, form, trigger]);

  useEffect(() => {
    const max =
      formModelYearValue && formModelYearValue > currentYear.current
        ? currentYear.current
        : formModelYearValue;
    if (formRoofYearValue && max && max > formRoofYearValue) {
      setValue("roofYearYyyy", undefined);
    }
  }, [formModelYearValue, formRoofYearValue, form]);

  useEffect(() => {
    if (formModelYearValue && !formRoofYearValue)
      setValue("roofYearYyyy", formModelYearValue);
  }, [formRoofYearValue, form, formModelYearValue]);

  useEffect(() => {
    if (!showHomeHudAndSkirtingType) {
      setValue("homeHudNumber", undefined);
      setValue("skirtingType", undefined);
    }
  }, [showHomeHudAndSkirtingType, setValue]);

  useEffect(() => {
    if (!showMakeCustom) {
      setValue("makeCustom", undefined);
    }
  }, [showMakeCustom, setValue]);

  useScrollToNextButton(isValid && !isSubmitSuccessful);

  return (
    <FormProvider {...form}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h4">Home Details</Typography>
        {!isTenantFlow && (
          <FormSectionHeading
            mb={2}
            title="Home Information"
            showDivider={false}
          />
        )}
        <Grid container spacing={3}>
          {!isTenantFlow && (
            <>
              <Grid item xs={12} sm={6}>
                <ManufacturerSelect />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormHomeTypeSelect
                  label={homeType.label}
                  control={control}
                  hasUnknown={false}
                />
              </Grid>
              {showMakeCustom && (
                <Grid item xs={12} mt={errors?.homeType ? 3 : undefined}>
                  <MakeCustomInput />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <ModelHomeInput />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormModelYearAutocomplete
                  inputStyle={{
                    padding: "0px !important",
                  }}
                  label={modelYear.label}
                  tooltip={modelYear.tooltip}
                  control={control}
                />
              </Grid>
              <FormSectionHeading title="Home Dimensions" />
              <Grid item xs={12}>
                <HomeSizeInput
                  label={totalSquareFootage.label}
                  tooltip={totalSquareFootage.tooltip}
                />
              </Grid>
              <FormSectionHeading title="Location & Property" />
            </>
          )}
          <Grid item xs={12}>
            <HomeLocationSelect
              label={location.label}
              tooltip={location.tooltip}
              isMyOwnLand={!isTenantFlow}
              disabled={isParkNameSectionDisabled}
            />
          </Grid>
          {showParkNameSection && (
            <Grid item xs={12}>
              <CommunityNamesSelect
                disabled={!!geoCodeResult?.communityName && !isBookConversion}
                stateCode={geoCodeResult.state}
              />
            </Grid>
          )}
          {isTenantFlow && (
            <>
              <Grid item xs={12}>
                <FormHomeTypeSelect
                  label={homeType.label}
                  control={control}
                  hasUnknown={false}
                />
              </Grid>
            </>
          )}
          {!isTenantFlow && (
            <>
              <FormSectionHeading title="Construction Details" />
              <Grid item xs={12} sm={6}>
                <RoofShapesSelect />
              </Grid>
              <Grid item xs={12} sm={6}>
                <RoofConstructionYearSelect
                  minYear={formModelYearValue}
                  label={roofYearYyyy.label}
                  tooltip={roofYearYyyy.tooltip}
                />
              </Grid>
              {showHomeHudAndSkirtingType && (
                <>
                  <Grid item xs={12} sm={6}>
                    <HomeHudNumberInput />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SkirtingTypeSelect />
                  </Grid>
                </>
              )}
              <FormSectionHeading title="Home Features" />
              <Grid item xs={12}>
                <Box mt={3} mb={-3} zIndex={2} position="relative">
                  <FixturesAndFinishesRadioGroup
                    showError={!formHomeFixturesValue && isBookConversion}
                  />
                </Box>
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <SubmitButton
              disabled={!isValid || loading}
              loading={loading}
              width={100}
              id={NEW_POLICY_SUBMIT_BUTTON_ID}>
              Next
            </SubmitButton>
          </Grid>
        </Grid>
      </Form>
    </FormProvider>
  );
}
